import { useEffect, useState, useRef, useCallback } from 'react';
import { Globe, Image, Upload, Save, Link2, Trash2, Plus, Eye, Home, BookOpen, Trophy, GripVertical, Share2, RefreshCw, Megaphone, MapPin, ScrollText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi, useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import SiteAddresses from '../components/SiteAddresses';
import ThemeAppearance from '../components/ThemeAppearance';
import { AMENITIES, PHOTO_SECTIONS } from '../utils/amenities';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputStyle = {
  background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
  borderRadius: 8, padding: '10px 14px', width: '100%', outline: 'none',
  fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box',
};

function Toggle({ enabled, onToggle, label, desc, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3" data-testid={testId}>
      <div>
        <p className="text-sm font-semibold" style={{ color: '#ccc' }}>{label}</p>
        {desc && <p className="text-xs" style={{ color: '#555' }}>{desc}</p>}
      </div>
      <button onClick={onToggle} data-testid={`${testId}-btn`}
        style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
          background: enabled ? '#E63946' : '#2A2A2A', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: enabled ? 22 : 2,
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </button>
    </div>
  );
}

export default function SportsSiteBuilderPage() {
  const { organization } = useSportsAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Theme & appearance — top-level settings fields (moved from Settings 2026-08-18)
  const [themePreset, setThemePreset] = useState('Velocity Red');
  const [siteMode, setSiteMode] = useState('booking_only');
  const [logoKey, setLogoKey] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);

  // Site builder config
  const [homeEnabled, setHomeEnabled] = useState(false);
  const [tournamentsPageEnabled, setTournamentsPageEnabled] = useState(false);
  const [homeHeadline, setHomeHeadline] = useState('');
  const [homeDescription, setHomeDescription] = useState('');
  const [socialLinks, setSocialLinks] = useState({ instagram: '', youtube: '', facebook: '', tiktok: '', website: '' });
  const [gallery, setGallery] = useState([]); // [{key, caption, url}]
  // Landing content sections (2026-07 redesign) — all optional, hidden on the
  // public page when empty
  const [announcement, setAnnouncement] = useState({ text: '', enabled: false });
  const [amenities, setAmenities] = useState([]); // list of AMENITIES ids
  const [directions, setDirections] = useState('');
  const [houseRules, setHouseRules] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();
  // Which section the next file-input pick belongs to (one hidden input serves
  // all section "Add" buttons)
  const uploadSectionRef = useRef('facility');
  // Social share card (Open Graph) — brand-locked, preview cache-buster only
  const [cardBust, setCardBust] = useState(Date.now());

  const fetchSettings = useCallback(async () => {
    try {
      const r = await sportsApi.get('/sports/settings');
      setThemePreset(r.data.theme_preset || 'Velocity Red');
      setSiteMode(r.data.site_mode || 'booking_only');
      setLogoKey(r.data.logo_key || '');
      setLogoUrl(r.data.logo_url || null);
      const sb = r.data.site_builder || {};
      setHomeEnabled(sb.home_enabled || false);
      setTournamentsPageEnabled(sb.tournaments_page_enabled || false);
      setHomeHeadline(sb.home_headline || '');
      setHomeDescription(sb.home_description || '');
      setSocialLinks(sb.social_links || { instagram: '', youtube: '', facebook: '', tiktok: '', website: '' });
      setAnnouncement(sb.announcement || { text: '', enabled: false });
      setAmenities(sb.amenities || []);
      setDirections(sb.directions || '');
      setHouseRules(sb.house_rules || '');
      // Gallery items need URL resolution
      const galleryItems = sb.gallery || [];
      // Resolve URLs for gallery items that have keys
      const resolved = [];
      for (const item of galleryItems) {
        if (item.key) {
          try {
            const urlRes = await sportsApi.get('/sports/settings/gallery-url', { params: { key: item.key } });
            resolved.push({ ...item, url: urlRes.data.url });
          } catch {
            resolved.push(item);
          }
        } else {
          resolved.push(item);
        }
      }
      setGallery(resolved);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await sportsApi.put('/sports/settings', {
        theme_preset: themePreset,
        site_mode: siteMode,
        logo_key: logoKey,
        site_builder: {
          home_enabled: homeEnabled,
          tournaments_page_enabled: tournamentsPageEnabled,
          home_headline: homeHeadline,
          home_description: homeDescription,
          social_links: socialLinks,
          gallery: gallery.map(g => ({ key: g.key, thumb_key: g.thumb_key || null, caption: g.caption || '', section: g.section || 'facility' })),
          announcement: { text: announcement.text || '', enabled: !!announcement.enabled },
          amenities,
          directions,
          house_rules: houseRules,
        },
      });
      toast.success('Site settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', 'photo');
      const res = await sportsApi.post('/sports/settings/upload-file', formData);
      setGallery(prev => [...prev, { key: res.data.key, thumb_key: res.data.thumb_key || null, caption: '', section: uploadSectionRef.current, url: URL.createObjectURL(file) }]);
      toast.success('Photo added');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingPhoto(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const removePhoto = (idx) => {
    setGallery(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCaption = (idx, caption) => {
    setGallery(prev => prev.map((g, i) => i === idx ? { ...g, caption } : g));
  };

  const slug = organization?.slug;
  const previewUrl = slug ? `${window.location.origin}/book/${slug}` : null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A' }}>
        <SportsNavBar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 80px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946', fontWeight: 700, marginBottom: 2 }}>Site Builder</p>
            <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>
              Multi-Page Microsite
            </h1>
            <p className="text-xs mt-1" style={{ color: '#555' }}>
              Expand your booking page into a full website with Home, Booking & Tournaments pages.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="site-builder-preview-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A', textDecoration: 'none' }}>
                <Eye className="h-3.5 w-3.5" /> Preview
              </a>
            )}
            <button onClick={handleSave} disabled={saving} data-testid="site-builder-save-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase"
              style={{ background: '#E63946', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Site addresses — permanent QR link + memorable web address */}
        <div className="mb-5">
          <SiteAddresses />
        </div>

        {/* Theme & appearance — public-site look (moved from Settings 2026-08-18) */}
        <ThemeAppearance
          theme={themePreset} siteMode={siteMode} logoUrl={logoUrl}
          onThemeChange={setThemePreset} onSiteModeChange={setSiteMode} onLogoKey={setLogoKey}
        />

        {/* Page Toggles */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-4">
            <Globe className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Pages</h2>
          </div>
          <p className="text-xs mb-3" style={{ color: '#555' }}>
            Enable pages to build a multi-page site. Booking page is always active.
          </p>
          <div style={{ borderTop: '1px solid #1A1A1A' }}>
            <div className="flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #1A1A1A' }}>
              <div style={{ background: '#E6394614', borderRadius: 8, padding: 8 }}>
                <BookOpen className="h-4 w-4" style={{ color: '#E63946' }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: '#ccc' }}>Booking</p>
                <p className="text-xs" style={{ color: '#555' }}>Court booking & availability — always active</p>
              </div>
              <span className="text-xs font-bold uppercase px-2 py-0.5 rounded" style={{ background: '#00E67618', color: '#00E676' }}>Always On</span>
            </div>
            <Toggle
              enabled={homeEnabled}
              onToggle={() => setHomeEnabled(v => !v)}
              label="Home Page"
              desc="Facility info, photos, social links — landing page for visitors"
              testId="site-builder-toggle-home"
            />
            <Toggle
              enabled={tournamentsPageEnabled}
              onToggle={() => setTournamentsPageEnabled(v => !v)}
              label="Tournaments Page"
              desc="Lists all tournaments with registration links"
              testId="site-builder-toggle-tournaments"
            />
          </div>
        </div>

        {/* Social Share Card (Open Graph — brand-locked, auto-generated) */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-1">
            <Share2 className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Social Share Card</h2>
            <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: '#00E67618', color: '#00E676' }}>Auto-generated</span>
          </div>
          <p className="text-xs mb-4" style={{ color: '#555' }}>
            This card appears when your booking link is shared on Facebook, Messenger or WhatsApp.
            It's generated automatically — your logo and facility name are placed on the AgriBooks Sports design.
            To change the logo, update it in <strong>Theme &amp; Appearance</strong> above.
          </p>
          {slug && (
            <img data-testid="site-builder-share-card-preview" alt="Share card preview"
              src={`${BACKEND}/api/sports/public/${slug}/og-card.png?t=${cardBust}`}
              style={{ width: '100%', borderRadius: 10, border: '1px solid #2A2A2A', display: 'block' }} />
          )}
          <button onClick={() => setCardBust(Date.now())} data-testid="site-builder-refresh-share-card"
            className="flex items-center gap-1.5 px-3 py-1.5 mt-3 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A', cursor: 'pointer' }}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh Preview
          </button>
        </div>

        {/* Announcement bar — shows at the very top of the public page */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-1">
            <Megaphone className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Announcement</h2>
          </div>
          <p className="text-xs mb-3" style={{ color: '#555' }}>
            A short notice pinned to the top of your public page — promos, holiday hours, court closures.
          </p>
          <Toggle
            enabled={announcement.enabled}
            onToggle={() => setAnnouncement(a => ({ ...a, enabled: !a.enabled }))}
            label="Show announcement"
            desc={announcement.enabled ? 'Visible to everyone on your page' : 'Hidden'}
            testId="site-builder-announcement-toggle"
          />
          {announcement.enabled && (
            <input value={announcement.text} onChange={e => setAnnouncement(a => ({ ...a, text: e.target.value }))}
              placeholder="e.g. Off-peak promo — ₱250/hr weekdays before 5 PM!"
              data-testid="site-builder-announcement-text" style={inputStyle} maxLength={200} />
          )}
        </div>

        {/* Amenities — icon picklist rendered as chips on the public page */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Amenities</h2>
          </div>
          <p className="text-xs mb-3" style={{ color: '#555' }}>
            Tap everything your facility offers — shown as icons on your page.
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(AMENITIES).map(([id, { label, Icon }]) => {
              const on = amenities.includes(id);
              return (
                <button key={id} data-testid={`site-builder-amenity-${id}`}
                  onClick={() => setAmenities(prev => on ? prev.filter(x => x !== id) : [...prev, id])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ background: on ? '#E6394618' : '#0A0A0A', color: on ? '#E63946' : '#888',
                    border: `1px solid ${on ? '#E6394650' : '#2A2A2A'}`, cursor: 'pointer' }}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Getting there + house rules */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Getting There &amp; House Rules</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Directions &amp; parking tips</label>
              <textarea value={directions} onChange={e => setDirections(e.target.value)}
                placeholder={"e.g. Beside the public market, look for the green gate.\nParking available inside — enter from the side street."}
                data-testid="site-builder-directions" rows={3}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} maxLength={500} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>House rules</label>
              <textarea value={houseRules} onChange={e => setHouseRules(e.target.value)}
                placeholder={"e.g. Non-marking shoes required.\nArrive 10 minutes before your slot."}
                data-testid="site-builder-house-rules" rows={3}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} maxLength={800} />
            </div>
          </div>
        </div>

        {/* Home Page Content (only if enabled) */}
        {(
          <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
            <div className="flex items-center gap-2 mb-4">
              <Home className="h-4 w-4" style={{ color: '#E63946' }} />
              <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Headline &amp; Description</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Headline</label>
                <input value={homeHeadline} onChange={e => setHomeHeadline(e.target.value)} placeholder="e.g. Premier Pickleball Experience"
                  data-testid="site-builder-headline" style={inputStyle} maxLength={120} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Description</label>
                <textarea value={homeDescription} onChange={e => setHomeDescription(e.target.value)}
                  placeholder="Tell visitors about your facility, what makes it special…"
                  data-testid="site-builder-description"
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} maxLength={1000} />
                <p className="text-right text-xs mt-1" style={{ color: '#444' }}>{homeDescription.length}/1000</p>
              </div>
            </div>
          </div>
        )}

        {/* Photos — sectioned slots; each section caps out until a photo is deleted */}
        <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-2 mb-1">
            <Image className="h-4 w-4" style={{ color: '#E63946' }} />
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Photos</h2>
          </div>
          <p className="text-xs mb-4" style={{ color: '#555' }}>
            Each section appears in its own spot on your page. When a section is full, delete a photo to add a new one.
          </p>
          <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          {Object.entries(PHOTO_SECTIONS).map(([sid, sec]) => {
            const items = gallery.map((g, idx) => ({ ...g, idx })).filter(g => (g.section || 'facility') === sid);
            const full = items.length >= sec.max;
            return (
              <div key={sid} className="mb-4 pb-4" style={{ borderBottom: '1px solid #1A1A1A' }} data-testid={`site-builder-photo-section-${sid}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#ccc' }}>{sec.label}
                      <span className="text-xs ml-2" style={{ color: full ? '#E63946' : '#555' }}>{items.length}/{sec.max}</span>
                    </p>
                    <p className="text-xs" style={{ color: '#555' }}>{sec.hint}</p>
                  </div>
                  <button onClick={() => { uploadSectionRef.current = sid; photoRef.current?.click(); }}
                    disabled={uploadingPhoto || full}
                    data-testid={`site-builder-add-photo-${sid}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                    style={{ background: full ? '#1A1A1A' : '#E6394618', color: full ? '#555' : '#E63946',
                      border: `1px solid ${full ? '#2A2A2A' : '#E6394630'}`, cursor: full ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                    <Plus className="h-3.5 w-3.5" /> {uploadingPhoto ? 'Uploading…' : full ? 'Full' : 'Add'}
                  </button>
                </div>
                {full && (
                  <p className="text-xs mb-2" style={{ color: '#E6394699' }}>Section full — delete a photo below to add a new one.</p>
                )}
                {items.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {items.map(item => (
                      <div key={item.idx} data-testid={`site-builder-gallery-item-${item.idx}`}
                        className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}>
                        {item.url ? (
                          <img src={item.url} alt="" style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 6, flexShrink: 0, background: '#1A1A1A' }} />
                        ) : (
                          <div style={{ width: 72, height: 54, borderRadius: 6, background: '#1A1A1A', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Image className="h-4 w-4" style={{ color: '#333' }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <input value={item.caption} onChange={e => updateCaption(item.idx, e.target.value)}
                            placeholder="Add a caption…"
                            data-testid={`site-builder-gallery-caption-${item.idx}`}
                            style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} maxLength={200} />
                        </div>
                        <button onClick={() => removePhoto(item.idx)} data-testid={`site-builder-gallery-remove-${item.idx}`}
                          style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Social Links (only if home enabled) */}
        {(
          <div className="rounded-xl p-5 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="h-4 w-4" style={{ color: '#E63946' }} />
              <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Social Links</h2>
            </div>
            <div className="space-y-3">
              {[
                { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourpage' },
                { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
                { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage' },
                { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourpage' },
                { key: 'website', label: 'Website', placeholder: 'https://yourwebsite.com' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>{label}</label>
                  <input value={socialLinks[key] || ''} onChange={e => setSocialLinks(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`site-builder-social-${key}`}
                    style={inputStyle} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save bar */}
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} data-testid="site-builder-save-bottom-btn"
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-bold uppercase"
            style={{ background: '#E63946', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
